<template>
    <div class="registration-page reg-root">
        <header class="reg-topbar">
            <button type="button" class="reg-back" @click="$router.go(-1)">
                <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
                <span class="reg-back-label">Torna indietro</span>
            </button>
            <img src="@/assets/logo.png" alt="Logo" class="reg-logo">
        </header>
        <div class="reg-shell">
            <h1 class="reg-title">Registrazione</h1>
            <form class="registration-form" @submit.prevent="register">
                <router-link class="reg-login-link"
                    :to="{ path: '/login', query: { returnUrl: $route.query.returnUrl || undefined } }">
                    Hai già un account? Accedi
                </router-link>
                <div class="row">
                    <div class="col-md-6">
                        <h4>Informazioni Personali</h4>
                        <div class="form-group">
                            <label class="visually-hidden" for="reg-firstName">Nome</label>
                            <input id="reg-firstName" v-model="user.firstName" type="text" placeholder="Nome *" class="form-control"
                                required @input="onFirstNameChange" />
                        </div>
                        <div class="form-group">
                            <label class="visually-hidden" for="reg-lastName">Cognome</label>
                            <input id="reg-lastName" v-model="user.lastName" type="text" placeholder="Cognome *" class="form-control"
                                required @input="onLastNameChange" />
                        </div>
                        <div class="form-group">
                            <Datepicker v-model="user.dateOfBirth" :format="format" :placeholder="'Data di nascita *'"
                                :aria-labels="{ input: 'Data di nascita' }"
                                :enable-time-picker="false" :auto-apply="true" :teleport="true"
                                input-class-name="form-control" :preview-format="previewFormat"
                                @update:model-value="updateDateOfBirth">
                                <template #action-select>
                                    <span class="action-select">Seleziona</span>
                                </template>
                            </Datepicker>
                        </div>
                        <div class="form-group">
                            <GenderSelect v-model="user.gender" @update:model-value="onGenderChange"></GenderSelect>
                        </div>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-country">Nazione</label>
                            <div class="country-dropdown-container" ref="countryContainer" @click.stop="toggleCountryDropdown">
                                <input id="reg-country" 
                                    :value="selectedCountryName"
                                    type="text" 
                                    placeholder="Nazione *" 
                                    class="form-control"
                                    required 
                                    readonly
                                    @click.stop="toggleCountryDropdown"
                                    @focus.prevent
                                    ref="countryInput"
                                />
                                <i class="fa fa-chevron-down country-dropdown-icon" :class="{ 'open': showCountryDropdown }" @click.stop="toggleCountryDropdown"></i>
                            </div>
                            <!-- Dropdown nazione con ricerca -->
                            <div v-if="showCountryDropdown" 
                                 class="country-dropdown-search"
                                 ref="countryDropdown"
                                 @mousedown.prevent
                                 @click.stop>
                                <input 
                                    type="text"
                                    aria-label="Cerca nazione"
                                    v-model="countrySearchQuery"
                                    placeholder="Cerca nazione..."
                                    class="form-control country-search-input"
                                    @input="filterCountries"
                                    @keydown.esc="closeCountryDropdown"
                                    @click.stop
                                    ref="countrySearchInput"
                                />
                                <div class="country-dropdown-list">
                                    <div v-if="filteredCountries.length === 0 && !isLoadingCountries" class="no-results">
                                        Nessun risultato trovato
                                    </div>
                                    <div v-if="isLoadingCountries" class="no-results">
                                        <i class="fa fa-spinner fa-spin"></i> Caricamento...
                                    </div>
                                    <div v-for="country in filteredCountries" 
                                         :key="country.code"
                                         class="country-dropdown-item"
                                         :class="{ 'selected': user.country === country.name }"
                                         @mousedown.prevent="selectCountry(country, $event)"
                                         @click.stop="selectCountry(country, $event)">
                                        {{ country.name }}
                                    </div>
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingCountries" class="country-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group position-relative" style="margin-top:15px;">
                            <label class="visually-hidden" for="reg-placeOfBirth">Luogo di nascita (opzionale)</label>
                            <input id="reg-placeOfBirth" 
                                v-model="user.placeOfBirth" 
                                type="text" 
                                placeholder="Luogo di nascita (opzionale)"
                                class="form-control"
                                @input="onPlaceOfBirthInput"
                                @focus="onPlaceOfBirthFocus"
                                @blur="onPlaceOfBirthBlur"
                                ref="placeOfBirthInput"
                            />
                            <!-- Dropdown suggerimenti luogo di nascita -->
                            <div v-if="showPlaceOfBirthSuggestions && placeOfBirthSuggestions.length > 0" 
                                 class="place-of-birth-suggestions-dropdown">
                                <div v-for="(suggestion, index) in placeOfBirthSuggestions" 
                                     :key="index"
                                     class="suggestion-item"
                                     @mousedown="selectPlaceOfBirth(suggestion)"
                                     @click="selectPlaceOfBirth(suggestion)">
                                    {{ suggestion.nome }}
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingPlaceOfBirth" class="place-of-birth-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-taxCode">Codice Fiscale</label>
                            <input id="reg-taxCode" 
                                v-model="user.taxCode" 
                                type="text" 
                                placeholder="Codice Fiscale *" 
                                class="form-control"
                                :class="{ 'is-invalid': taxCodeError }"
                                required 
                                @input="onTaxCodeInput"
                                @blur="onTaxCodeBlur"
                                style="text-transform: uppercase;"
                            />
                            <span v-if="taxCodeError" class="text-danger small d-block mt-1">
                                <i class="fa fa-exclamation-circle"></i> {{ taxCodeError }}
                            </span>
                            <span v-else-if="user.taxCode && isItaly && user.taxCode.length === 16 && !taxCodeError" class="text-success small d-block mt-1">
                                <i class="fa fa-check-circle"></i> Codice fiscale valido
                            </span>
                        </div>
                    </div>
                    <div class="col-md-6 mt-4 mt-sm-0">
                        <h4>Recapiti</h4>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-address">Indirizzo (opzionale)</label>
                            <input id="reg-address" 
                                v-model="user.address" 
                                type="text" 
                                placeholder="Indirizzo (opzionale)" 
                                class="form-control"
                                @input="onAddressInput"
                                @focus="onAddressFocus"
                                @blur="onAddressBlur"
                                ref="addressInput"
                            />
                            <!-- Dropdown suggerimenti indirizzi -->
                            <div v-if="showAddressSuggestions && addressSuggestions.length > 0" 
                                 class="address-suggestions-dropdown">
                                <div v-for="(suggestion, index) in addressSuggestions" 
                                     :key="index"
                                     class="suggestion-item"
                                     @mousedown="selectAddress(suggestion)"
                                     @click="selectAddress(suggestion)">
                                    {{ suggestion.description }}
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingAddresses" class="address-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-city">Città</label>
                            <input id="reg-city" 
                                v-model="user.city" 
                                type="text" 
                                placeholder="Città *" 
                                class="form-control" 
                                required 
                                @input="onCityInput"
                                @focus="onCityFocus"
                                @blur="onCityBlur"
                                ref="cityInput"
                            />
                            <!-- Dropdown suggerimenti città ISTAT -->
                            <div v-if="showCitySuggestions && citySuggestions.length > 0" 
                                 class="city-suggestions-dropdown">
                                <div v-for="(suggestion, index) in citySuggestions" 
                                     :key="index"
                                     class="suggestion-item"
                                     @mousedown="selectCity(suggestion)"
                                     @click="selectCity(suggestion)">
                                    {{ suggestion.nome }}
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingCities" class="city-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-postalCode">CAP</label>
                            <input id="reg-postalCode" 
                                v-model="user.postalCode" 
                                type="text" 
                                placeholder="CAP *" 
                                class="form-control"
                                required 
                                @input="onPostalCodeInput"
                                @focus="onPostalCodeFocus"
                                @blur="onPostalCodeBlur"
                                ref="postalCodeInput"
                            />
                            <!-- Dropdown suggerimenti CAP -->
                            <div v-if="showPostalCodeSuggestions && postalCodeSuggestions.length > 0" 
                                 class="postal-code-suggestions-dropdown">
                                <div v-for="(suggestion, index) in postalCodeSuggestions" 
                                     :key="index"
                                     class="suggestion-item"
                                     @mousedown="selectPostalCode(suggestion)"
                                     @click="selectPostalCode(suggestion)">
                                    {{ suggestion.codice }}
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingPostalCodes" class="postal-code-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group position-relative">
                            <label class="visually-hidden" for="reg-province">Provincia</label>
                            <input id="reg-province" 
                                v-model="user.province" 
                                type="text" 
                                placeholder="Provincia *" 
                                class="form-control"
                                required 
                                @input="onProvinceInput"
                                @focus="onProvinceFocus"
                                @blur="onProvinceBlur"
                                ref="provinceInput"
                            />
                            <!-- Dropdown suggerimenti provincia -->
                            <div v-if="showProvinceSuggestions && provinceSuggestions.length > 0" 
                                 class="province-suggestions-dropdown">
                                <div v-for="(suggestion, index) in provinceSuggestions" 
                                     :key="index"
                                     class="suggestion-item"
                                     @mousedown="selectProvince(suggestion)"
                                     @click="selectProvince(suggestion)">
                                    {{ suggestion.nome }}
                                </div>
                            </div>
                            <!-- Loading indicator -->
                            <div v-if="isLoadingProvinces" class="province-loading">
                                <i class="fa fa-spinner fa-spin"></i> Caricamento...
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="visually-hidden" for="reg-phoneNumber">Telefono</label>
                            <input id="reg-phoneNumber" v-model="user.phoneNumber" type="text" placeholder="Telefono *" class="form-control" />
                        </div>
                    </div>
                </div>
                <!-- Sezione Tutore legale: visibile solo se minorenne -->
                <div v-if="isMinor" class="d-flex justify-content-between align-items-center mb-1 mt-4">
                    <h4><b>3.</b> Tutore legale</h4>
                </div>
                <div v-if="isMinor">
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-firstName">Nome tutore</label>
                        <input id="reg-guardian-firstName" v-model="user.legalGuardian.firstName" type="text" placeholder="Nome tutore *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-lastName">Cognome tutore</label>
                        <input id="reg-guardian-lastName" v-model="user.legalGuardian.lastName" type="text" placeholder="Cognome tutore *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-taxCode">Codice fiscale tutore</label>
                        <input id="reg-guardian-taxCode" v-model="user.legalGuardian.taxCode" type="text" placeholder="Codice fiscale tutore *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-relationship">Relazione con il minore (es. Genitore)</label>
                        <input id="reg-guardian-relationship" v-model="user.legalGuardian.relationship" type="text" placeholder="Relazione con il minore (es. Genitore) *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-email">Email tutore</label>
                        <input id="reg-guardian-email" v-model="user.legalGuardian.email" type="email" placeholder="Email tutore *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-phoneNumber">Telefono tutore</label>
                        <input id="reg-guardian-phoneNumber" v-model="user.legalGuardian.phoneNumber" type="text" placeholder="Telefono tutore *" class="form-control" :required="isMinor" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-address">Indirizzo (opzionale) del tutore</label>
                        <input id="reg-guardian-address" v-model="user.legalGuardian.address" type="text" placeholder="Indirizzo (opzionale)" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-city">Città (opzionale) del tutore</label>
                        <input id="reg-guardian-city" v-model="user.legalGuardian.city" type="text" placeholder="Città (opzionale)" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-postalCode">CAP (opzionale) del tutore</label>
                        <input id="reg-guardian-postalCode" v-model="user.legalGuardian.postalCode" type="text" placeholder="CAP (opzionale)" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-province">Provincia (opzionale) del tutore</label>
                        <input id="reg-guardian-province" v-model="user.legalGuardian.province" type="text" placeholder="Provincia (opzionale)" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label class="visually-hidden" for="reg-guardian-country">Nazione (opzionale) del tutore</label>
                        <input id="reg-guardian-country" v-model="user.legalGuardian.country" type="text" placeholder="Nazione (opzionale)" class="form-control" />
                    </div>
                </div>

                <h4 class="mt-4">Crea un account</h4>
                <div class="form-group">
                    <label class="visually-hidden" for="reg-email">Email</label>
                    <input id="reg-email" v-model="user.email" type="email" placeholder="Email *" class="form-control"
                        @blur="checkEmailExists" :class="{ 'is-invalid': emailError }" required />
                    <span v-if="emailError" class="text-danger">{{ emailError }}</span>
                </div>
                <div class="form-group position-relative">
                    <label class="visually-hidden" for="reg-password">Password</label>
                    <input id="reg-password" v-model="user.password" :type="showPassword ? 'text' : 'password'" placeholder="Password *"
                        class="form-control" @blur="checkPassword" :class="{ 'is-invalid': passwordError }" required />
                    <span v-if="passwordError" class="text-danger">{{ passwordError }}</span>
                    <button type="button" class="toggle-password" aria-controls="reg-password"
                        :aria-label="showPassword ? 'Nascondi password' : 'Mostra password'"
                        @click="togglePasswordVisibility">
                        {{ showPassword ? 'Nascondi' : 'Mostra' }}
                    </button>
                </div>
                <div class="terms-section mt-4">
                    <!-- 1. Termini e condizioni -->
                    <div class="terms-checkbox">
                        <input type="checkbox" id="terms" class="custom-checkbox terms-cb"
                            v-model="acceptTerms" />
                        <div class="text-container">
                            <label for="terms" class="terms-label">
                                Confermo di aver preso visione ed accetto i
                                <a href="https://elitefitness.it/terms/" target="_blank">Termini
                                    e condizioni</a>
                            </label>
                        </div>
                    </div>

                    <!-- 2. Informativa privacy -->
                    <div class="terms-checkbox">
                        <input type="checkbox" id="privacy" class="custom-checkbox terms-cb"
                            v-model="acceptPrivacyPolicy" />
                        <div class="text-container">
                            <label for="privacy" class="terms-label">
                                Confermo di aver preso visione dell'
                                <a href="https://elitefitness.it/privacy/"
                                    target="_blank">Informativa Privacy</a>
                                ai sensi del Regolamento (UE) 2016/679 (GDPR)
                            </label>
                        </div>
                    </div>

                    <!-- 3. Regolamento -->
                    <div class="terms-checkbox">
                        <input type="checkbox" id="regulation" class="custom-checkbox terms-cb"
                            v-model="acceptRegulation" />
                        <div class="text-container">
                            <label for="regulation" class="terms-label">
                                Confermo di aver preso visione ed accetto il
                                <a href="https://elitefitness.it/regulation/"
                                    target="_blank">Regolamento Interno di Elite
                                    Fitness SSD</a>
                            </label>
                        </div>
                    </div>

                    <!-- 4. Codice etico -->
                    <div class="terms-checkbox">
                        <input type="checkbox" id="medicalCert"
                            class="custom-checkbox terms-cb"
                            v-model="acceptMedicalCertDeclaration" />
                        <div class="text-container">
                            <label for="medicalCert" class="terms-label">
                                Confermo di aver preso visione del
                                <a href="https://elitefitness.it/ethical-code/"
                                    target="_blank">Codice Etico</a>
                            </label>
                        </div>
                    </div>

                    <!-- 5. Documenti di identità -->
                    <div class="terms-checkbox">
                        <input type="checkbox" id="identityDocs"
                            class="custom-checkbox terms-cb"
                            v-model="acceptIdentityDocsDeclaration" />
                        <div class="text-container">
                            <label for="identityDocs" class="terms-label">
                                Confermo di aver preso visione della
                                <a href="https://elitefitness.it/dichiarazione-documenti-di-identita/"
                                    target="_blank">Dichiarazione
                                    Relativa ai Documenti di
                                    Identità</a>
                            </label>
                        </div>
                    </div>

                </div>
                <div class="text-center">
                    <span v-if="!hasAcceptedTerms" class="terms-warning">Per
                        proseguire,
                        devi
                        accettare i termini e le
                        condizioni.</span>
                </div>
                <div class="text-center mt-4">
                    <button type="submit" class="btn btn-custom" :disabled="isLoading || !hasAcceptedTerms"
                        :class="{ 'loading': isLoading }">
                        <span v-if="!isLoading">Registrati</span>
                        <span v-else>
                            <span class="loading-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import '@/assets/css/registration-checkout.css'
import GenderSelect from '@/components/GenderSelect.vue';
import Swal from 'sweetalert2'
import { locationService } from '@/config/api.js'
import { validateItalianTaxCode } from '@/utils/taxCodeValidator.js'
import { buildRegistrationSigningAttachments, dateOfBirthToApiIso } from '@/utils/registrationSigningFlow.js'

export default defineComponent({
    components: {
        GenderSelect,
        Datepicker
    },
    data() {
        return {
            user: {
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                dateOfBirth: null,
                placeOfBirth: '',
                taxCode: '',
                gender: '',
                address: '',
                city: '',
                postalCode: '',
                province: '',
                country: '',
                phoneNumber: '',
                mobileNumber: '',
                // Dati del tutore legale (compilati solo se minore)
                legalGuardian: {
                    firstName: '',
                    lastName: '',
                    taxCode: '',
                    relationship: '',
                    email: '',
                    phoneNumber: '',
                    address: '',
                    city: '',
                    postalCode: '',
                    province: '',
                    country: ''
                }
            },
            emailError: null,
            passwordError: null,
            taxCodeError: null, // Errore validazione codice fiscale
            showPassword: false, // Stato della visibilità della password
            isLoading: false, // Stato di caricamento
            taxCodeValidationTimeout: null, // Timeout per debounce validazione CF
            
            // Autocompletamento indirizzi
            addressSuggestions: [],
            showAddressSuggestions: false,
            isLoadingAddresses: false,
            addressSearchTimeout: null,
            
            // Autocompletamento città ISTAT
            citySuggestions: [],
            showCitySuggestions: false,
            isLoadingCities: false,
            citySearchTimeout: null,
            
            // Autocompletamento luogo di nascita ISTAT
            placeOfBirthSuggestions: [],
            showPlaceOfBirthSuggestions: false,
            isLoadingPlaceOfBirth: false,
            placeOfBirthSearchTimeout: null,
            
            // Autocompletamento CAP ISTAT
            postalCodeSuggestions: [],
            showPostalCodeSuggestions: false,
            isLoadingPostalCodes: false,
            postalCodeSearchTimeout: null,
            
            // Autocompletamento provincia ISTAT
            provinceSuggestions: [],
            showProvinceSuggestions: false,
            isLoadingProvinces: false,
            provinceSearchTimeout: null,
            
            // Dropdown nazione con ricerca
            allCountries: [],
            countrySearchQuery: '',
            showCountryDropdown: false,
            isLoadingCountries: false,
            format: 'dd/MM/yyyy',
            previewFormat: (date) => {
                if (!date) return ''
                const day = date.getDate().toString().padStart(2, '0')
                const month = (date.getMonth() + 1).toString().padStart(2, '0')
                const year = date.getFullYear()
                return `${day}/${month}/${year}`
            },
            
            // Checkbox consensi
            acceptTerms: false,
            acceptPrivacyPolicy: false,
            acceptRegulation: false,
            acceptMedicalCertDeclaration: false,
            acceptIdentityDocsDeclaration: false
        };
    },
    computed: {
        // Rileva se l'utente è minorenne in base alla data di nascita
        isMinor() {
            const dob = this.user?.dateOfBirth;
            if (!dob) return false;
            try {
                const d = dob instanceof Date ? dob : new Date(dob);
                if (Number.isNaN(d.getTime())) return false;
                const today = new Date();
                let age = today.getFullYear() - d.getFullYear();
                const m = today.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
                return age < 18;
            } catch (_) {
                return false;
            }
        },
        
        // Verifica se la nazione selezionata è Italia
        isItaly() {
            return this.user.country === 'Italy' || this.user.country === 'Italia';
        },
        
        // Nome del paese selezionato per il display
        selectedCountryName: {
            get() {
                if (this.user.country) {
                    return this.user.country;
                }
                return '';
            },
            // eslint-disable-next-line no-unused-vars
            set(_value) {
                // Il valore viene impostato tramite selectCountry
            }
        },
        
        // Filtra i paesi in base alla ricerca
        filteredCountries() {
            if (!this.countrySearchQuery || this.countrySearchQuery.trim() === '') {
                return this.allCountries;
            }
            const query = this.countrySearchQuery.toLowerCase().trim();
            return this.allCountries.filter(country => 
                country.name.toLowerCase().includes(query)
            );
        },
        
        // Verifica se tutti i consensi sono stati accettati
        hasAcceptedTerms() {
            return this.acceptTerms && this.acceptPrivacyPolicy && this.acceptRegulation && this.acceptMedicalCertDeclaration && this.acceptIdentityDocsDeclaration;
        }
    },
    watch: {
        // ✅ AGGIUNTO: Watcher per i termini (doppio consenso)
        hasAcceptedTerms(newValue) {
            if (newValue) {
                // Quando tutte le checkbox di registrazione sono spuntate, mostra popup di conferma
                const riepilogo = `<ul style='text-align:left;line-height:1.5;padding-left:18px;'>`
                  + `<li>Accetto Termini e Condizioni</li>`
                  + `<li>Ho letto e accetto l'Informativa Privacy</li>`
                  + `<li>Accetto il Regolamento Interno</li>`
                  + `<li>Ho letto e accetto il Codice Etico</li>`
                  + `<li>Confermo di aver preso visione della dichiarazione relativa ai documenti di identità</li>`
                  + `</ul>`;
                Swal.fire({
                    title: 'Confermi tutte le dichiarazioni?',
                    html: `<div style='text-align:left;font-size:15px;'>Stai per procedere alla registrazione avendo dichiarato e accettato quanto segue:${riepilogo}<p style='margin-top:8px;'>Confermi?</p></div>`,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Conferma',
                    cancelButtonText: 'Annulla',
                    reverseButtons: true,
                    focusCancel: true,
                    customClass: {
                        confirmButton: 'swal-stopgo-confirm',
                        cancelButton: 'swal-stopgo-cancel',
                        actions: 'swal-stopgo-actions'
                    },
                    buttonsStyling: false
                }).then(res => {
                    if (res.isConfirmed) {
                        // Conferma: le checkbox rimangono spuntate
                    } else {
                        // Reset di tutte le checkbox termini
                        this.acceptTerms = false;
                        this.acceptPrivacyPolicy = false;
                        this.acceptRegulation = false;
                        this.acceptMedicalCertDeclaration = false;
                        this.acceptIdentityDocsDeclaration = false;
                    }
                });
            }
        }
    },
    methods: {
        // Metodi per autocompletamento indirizzi
        onAddressInput() {
            this.clearAddressTimeout();
            if (this.user.address.length >= 3) {
                this.addressSearchTimeout = setTimeout(() => {
                    this.searchAddresses(this.user.address);
                }, 300);
            } else {
                this.showAddressSuggestions = false;
                this.addressSuggestions = [];
            }
        },
        
        onAddressFocus() {
            if (this.addressSuggestions.length > 0) {
                this.showAddressSuggestions = true;
            }
        },
        
        onAddressBlur() {
            // Delay per permettere il click sui suggerimenti
            setTimeout(() => {
                this.showAddressSuggestions = false;
            }, 200);
        },
        
        async searchAddresses(query) {
            if (!query || query.length < 3) return;
            
            this.isLoadingAddresses = true;
            try {
                const result = await locationService.searchAddresses(query);
                
                if (result.success && result.data) {
                    this.addressSuggestions = result.data;
                    this.showAddressSuggestions = true;
                } else {
                    this.addressSuggestions = [];
                    this.showAddressSuggestions = false;
                }
            } catch (error) {
                console.error('Errore nella ricerca indirizzi:', error);
                this.addressSuggestions = [];
                this.showAddressSuggestions = false;
            } finally {
                this.isLoadingAddresses = false;
            }
        },
        
        async selectAddress(suggestion) {
            this.user.address = suggestion.description;
            this.showAddressSuggestions = false;
            this.addressSuggestions = [];
            
            // Ottieni i dettagli completi dell'indirizzo
            try {
                const result = await locationService.getLocationDetails(suggestion.placeId);
                
                if (result.success && result.data) {
                    const details = result.data;
                    // Aggiorna automaticamente i campi correlati
                    if (details.city) this.user.city = details.city;
                    if (details.postalCode) this.user.postalCode = details.postalCode;
                    if (details.province) this.user.province = details.province;
                }
            } catch (error) {
                console.error('Errore nel recupero dettagli indirizzo:', error);
            }
        },
        
        clearAddressTimeout() {
            if (this.addressSearchTimeout) {
                clearTimeout(this.addressSearchTimeout);
                this.addressSearchTimeout = null;
            }
        },
        
        // Metodi per autocompletamento città
        onCityInput() {
            this.clearCityTimeout();
            // Abilita autocompletamento ISTAT solo se la nazione è Italy
            if (!this.isItaly) {
                this.showCitySuggestions = false;
                this.citySuggestions = [];
                return;
            }
            if (this.user.city.length >= 2) {
                this.citySearchTimeout = setTimeout(() => {
                    this.searchCities(this.user.city);
                }, 300);
            } else {
                this.showCitySuggestions = false;
                this.citySuggestions = [];
            }
        },
        
        onCityFocus() {
            // Mostra suggerimenti solo se è Italy
            if (this.isItaly && this.citySuggestions.length > 0) {
                this.showCitySuggestions = true;
            }
        },
        
        onCityBlur() {
            // Delay per permettere il click sui suggerimenti
            setTimeout(() => {
                this.showCitySuggestions = false;
            }, 200);
        },
        
        async searchCities(query) {
            if (!query || query.length < 2) return;
            
            this.isLoadingCities = true;
            try {
                const response = await fetch(`https://api.elitefitness.it/api/open/istat/comuni/search?nome=${encodeURIComponent(query)}`);
                const result = await response.json();
                
                if (result.success && result.data) {
                    this.citySuggestions = result.data;
                    this.showCitySuggestions = true;
                } else {
                    this.citySuggestions = [];
                    this.showCitySuggestions = false;
                }
            } catch (error) {
                console.error('Errore nella ricerca città ISTAT:', error);
                this.citySuggestions = [];
                this.showCitySuggestions = false;
            } finally {
                this.isLoadingCities = false;
            }
        },
        
        async selectCity(suggestion) {
            this.user.city = suggestion.nome;
            this.showCitySuggestions = false;
            this.citySuggestions = [];
            
            // Aggiorna automaticamente i campi correlati con i dati ISTAT
            if (suggestion.provincia && suggestion.provincia.nome) {
                this.user.province = suggestion.provincia.nome;
            }
            if (suggestion.cap && suggestion.cap.length > 0) {
                this.user.postalCode = suggestion.cap[0]; // Prendi il primo CAP
            }
        },
        
        clearCityTimeout() {
            if (this.citySearchTimeout) {
                clearTimeout(this.citySearchTimeout);
                this.citySearchTimeout = null;
            }
        },
        
        // Metodi per autocompletamento luogo di nascita ISTAT
        onPlaceOfBirthInput() {
            this.clearPlaceOfBirthTimeout();
            if (this.user.placeOfBirth.length >= 2) {
                this.placeOfBirthSearchTimeout = setTimeout(() => {
                    this.searchPlaceOfBirth(this.user.placeOfBirth);
                }, 300);
            } else {
                this.showPlaceOfBirthSuggestions = false;
                this.placeOfBirthSuggestions = [];
            }
        },
        
        onPlaceOfBirthFocus() {
            if (this.placeOfBirthSuggestions.length > 0) {
                this.showPlaceOfBirthSuggestions = true;
            }
        },
        
        onPlaceOfBirthBlur() {
            setTimeout(() => {
                this.showPlaceOfBirthSuggestions = false;
            }, 200);
        },
        
        async searchPlaceOfBirth(query) {
            if (!query || query.length < 2) return;
            
            this.isLoadingPlaceOfBirth = true;
            try {
                const response = await fetch(`https://api.elitefitness.it/api/open/istat/comuni/search?nome=${encodeURIComponent(query)}`);
                const result = await response.json();
                
                if (result.success && result.data) {
                    this.placeOfBirthSuggestions = result.data;
                    this.showPlaceOfBirthSuggestions = true;
                } else {
                    this.placeOfBirthSuggestions = [];
                    this.showPlaceOfBirthSuggestions = false;
                }
            } catch (error) {
                console.error('Errore nella ricerca luogo di nascita ISTAT:', error);
                this.placeOfBirthSuggestions = [];
                this.showPlaceOfBirthSuggestions = false;
            } finally {
                this.isLoadingPlaceOfBirth = false;
            }
        },
        
        selectPlaceOfBirth(suggestion) {
            this.user.placeOfBirth = suggestion.nome;
            this.showPlaceOfBirthSuggestions = false;
            this.placeOfBirthSuggestions = [];
        },
        
        clearPlaceOfBirthTimeout() {
            if (this.placeOfBirthSearchTimeout) {
                clearTimeout(this.placeOfBirthSearchTimeout);
                this.placeOfBirthSearchTimeout = null;
            }
        },
        
        // Metodi per autocompletamento CAP ISTAT
        onPostalCodeInput() {
            this.clearPostalCodeTimeout();
            // Abilita autocompletamento ISTAT solo se la nazione è Italy
            if (!this.isItaly) {
                this.showPostalCodeSuggestions = false;
                this.postalCodeSuggestions = [];
                return;
            }
            if (this.user.postalCode.length >= 3) {
                this.postalCodeSearchTimeout = setTimeout(() => {
                    this.searchPostalCodes(this.user.postalCode);
                }, 300);
            } else {
                this.showPostalCodeSuggestions = false;
                this.postalCodeSuggestions = [];
            }
        },
        
        onPostalCodeFocus() {
            // Mostra suggerimenti solo se è Italy
            if (this.isItaly && this.postalCodeSuggestions.length > 0) {
                this.showPostalCodeSuggestions = true;
            }
        },
        
        onPostalCodeBlur() {
            setTimeout(() => {
                this.showPostalCodeSuggestions = false;
            }, 200);
        },
        
        async searchPostalCodes(query) {
            if (!query || query.length < 3) return;
            
            this.isLoadingPostalCodes = true;
            try {
                const response = await fetch(`https://api.elitefitness.it/api/open/istat/cap/search?codice=${encodeURIComponent(query)}`);
                const result = await response.json();
                
                if (result.success && result.data) {
                    this.postalCodeSuggestions = result.data;
                    this.showPostalCodeSuggestions = true;
                } else {
                    this.postalCodeSuggestions = [];
                    this.showPostalCodeSuggestions = false;
                }
            } catch (error) {
                console.error('Errore nella ricerca CAP ISTAT:', error);
                this.postalCodeSuggestions = [];
                this.showPostalCodeSuggestions = false;
            } finally {
                this.isLoadingPostalCodes = false;
            }
        },
        
        selectPostalCode(suggestion) {
            this.user.postalCode = suggestion.codice;
            this.showPostalCodeSuggestions = false;
            this.postalCodeSuggestions = [];
            
            // Aggiorna automaticamente i campi correlati
            if (suggestion.comune && suggestion.comune.nome) {
                this.user.city = suggestion.comune.nome;
            }
            if (suggestion.provincia && suggestion.provincia.nome) {
                this.user.province = suggestion.provincia.nome;
            }
        },
        
        clearPostalCodeTimeout() {
            if (this.postalCodeSearchTimeout) {
                clearTimeout(this.postalCodeSearchTimeout);
                this.postalCodeSearchTimeout = null;
            }
        },
        
        // Metodi per autocompletamento provincia ISTAT
        onProvinceInput() {
            this.clearProvinceTimeout();
            // Abilita autocompletamento ISTAT solo se la nazione è Italy
            if (!this.isItaly) {
                this.showProvinceSuggestions = false;
                this.provinceSuggestions = [];
                return;
            }
            if (this.user.province.length >= 2) {
                this.provinceSearchTimeout = setTimeout(() => {
                    this.searchProvinces(this.user.province);
                }, 300);
            } else {
                this.showProvinceSuggestions = false;
                this.provinceSuggestions = [];
            }
        },
        
        onProvinceFocus() {
            // Mostra suggerimenti solo se è Italy
            if (this.isItaly && this.provinceSuggestions.length > 0) {
                this.showProvinceSuggestions = true;
            }
        },
        
        onProvinceBlur() {
            setTimeout(() => {
                this.showProvinceSuggestions = false;
            }, 200);
        },
        
        async searchProvinces(query) {
            if (!query || query.length < 2) return;
            
            this.isLoadingProvinces = true;
            try {
                const response = await fetch(`https://api.elitefitness.it/api/open/istat/province/search?nome=${encodeURIComponent(query)}`);
                const result = await response.json();
                
                if (result.success && result.data) {
                    this.provinceSuggestions = result.data;
                    this.showProvinceSuggestions = true;
                } else {
                    this.provinceSuggestions = [];
                    this.showProvinceSuggestions = false;
                }
            } catch (error) {
                console.error('Errore nella ricerca province ISTAT:', error);
                this.provinceSuggestions = [];
                this.showProvinceSuggestions = false;
            } finally {
                this.isLoadingProvinces = false;
            }
        },
        
        selectProvince(suggestion) {
            this.user.province = suggestion.nome;
            this.showProvinceSuggestions = false;
            this.provinceSuggestions = [];
        },
        
        clearProvinceTimeout() {
            if (this.provinceSearchTimeout) {
                clearTimeout(this.provinceSearchTimeout);
                this.provinceSearchTimeout = null;
            }
        },
        
        // Metodi per dropdown nazione con ricerca
        async loadAllCountries() {
            this.isLoadingCountries = true;
            try {
                const response = await fetch('https://api.elitefitness.it/api/open/istat/countries/all');
                const result = await response.json();
                
                if (result.success && result.data) {
                    this.allCountries = result.data;
                    // Ordina per nome
                    this.allCountries.sort((a, b) => a.name.localeCompare(b.name));
                } else {
                    this.allCountries = [];
                }
            } catch (error) {
                console.error('Errore nel caricamento paesi:', error);
                this.allCountries = [];
            } finally {
                this.isLoadingCountries = false;
            }
        },
        
        toggleCountryDropdown(event) {
            // Previeni la propagazione
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            
            if (!this.showCountryDropdown) {
                this.openCountryDropdown();
            } else {
                this.closeCountryDropdown();
            }
        },
        
        openCountryDropdown() {
            this.showCountryDropdown = true;
            this.countrySearchQuery = '';
            // Carica i paesi se non sono ancora stati caricati
            if (this.allCountries.length === 0) {
                this.loadAllCountries();
            }
            // Aggiungi listener per click esterni solo dopo che il DOM è stato aggiornato
            this.$nextTick(() => {
                // Focus sul campo di ricerca
                if (this.$refs.countrySearchInput) {
                    this.$refs.countrySearchInput.focus();
                }
                // Aggiungi listener per click esterni con un delay per evitare che il click corrente lo chiuda
                setTimeout(() => {
                    document.addEventListener('mousedown', this.handleClickOutside, true);
                }, 250);
            });
        },
        
        closeCountryDropdown() {
            this.showCountryDropdown = false;
            this.countrySearchQuery = '';
            // Rimuovi listener per click esterni
            document.removeEventListener('mousedown', this.handleClickOutside, true);
        },
        
        handleClickOutside(event) {
            // Non chiudere se il dropdown non è aperto
            if (!this.showCountryDropdown) {
                return;
            }
            
            // Verifica se il click è dentro il container o il dropdown
            const container = this.$refs.countryContainer;
            const dropdown = this.$refs.countryDropdown;
            
            if (!container) {
                return;
            }
            
            // Se il click è dentro il container o il dropdown, non fare nulla
            if (container.contains(event.target) || (dropdown && dropdown.contains(event.target))) {
                return;
            }
            
            // Se siamo qui, il click è fuori, quindi chiudi
            this.closeCountryDropdown();
        },
        
        selectCountry(country, event) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
            this.user.country = country.name;
            this.closeCountryDropdown();
            // Se il paese selezionato non è Italia, pulisci i suggerimenti ISTAT
            if (country.code !== 'IT') {
                this.citySuggestions = [];
                this.postalCodeSuggestions = [];
                this.provinceSuggestions = [];
                this.showCitySuggestions = false;
                this.showPostalCodeSuggestions = false;
                this.showProvinceSuggestions = false;
                // Pulisci anche l'errore CF se non è Italia
                if (!this.isItaly) {
                    this.taxCodeError = null;
                }
            }
            // Se diventa Italia e c'è già un CF inserito, valida
            if (country.code === 'IT' && this.user.taxCode && this.user.taxCode.length === 16) {
                this.validateTaxCode();
            }
        },
        
        // Metodi per validazione codice fiscale
        clearTaxCodeValidationTimeout() {
            if (this.taxCodeValidationTimeout) {
                clearTimeout(this.taxCodeValidationTimeout);
                this.taxCodeValidationTimeout = null;
            }
        },
        
        onTaxCodeInput() {
            // Converti a maiuscolo
            this.user.taxCode = this.user.taxCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
            
            // Se non è Italia, non validare
            if (!this.isItaly) {
                this.taxCodeError = null;
                return;
            }
            
            // Se il CF è vuoto, pulisci l'errore
            if (!this.user.taxCode || this.user.taxCode.trim() === '') {
                this.taxCodeError = null;
                this.clearTaxCodeValidationTimeout();
                return;
            }
            
            // Se il CF ha raggiunto 16 caratteri, valida immediatamente (senza debounce)
            if (this.user.taxCode.length === 16) {
                this.clearTaxCodeValidationTimeout();
                // Valida immediatamente
                this.validateTaxCode();
            } else if (this.user.taxCode.length < 16) {
                // Durante la digitazione (prima di raggiungere 16 caratteri), pulisci l'errore
                this.taxCodeError = null;
                this.clearTaxCodeValidationTimeout();
            } else {
                // Se supera 16 caratteri, mostra errore immediato
                this.clearTaxCodeValidationTimeout();
                this.taxCodeError = 'Il codice fiscale deve contenere esattamente 16 caratteri';
            }
        },
        
        onTaxCodeBlur() {
            this.clearTaxCodeValidationTimeout();
            
            // Se non è Italia, non validare
            if (!this.isItaly) {
                this.taxCodeError = null;
                return;
            }
            
            // Valida quando l'utente esce dal campo
            if (this.user.taxCode && this.user.taxCode.trim() !== '') {
                // Se ha meno di 16 caratteri, mostra errore immediato
                if (this.user.taxCode.length < 16) {
                    this.taxCodeError = `Il codice fiscale deve contenere esattamente 16 caratteri. Caratteri inseriti: ${this.user.taxCode.length}`;
                    return;
                }
                // Se ha 16 caratteri, valida completamente
                this.validateTaxCode();
            }
        },
        
        validateTaxCode() {
            if (!this.isItaly) {
                this.taxCodeError = null;
                return;
            }
            
            if (!this.user.taxCode || this.user.taxCode.trim() === '') {
                this.taxCodeError = null;
                return;
            }
            
            try {
                const userData = {
                    firstName: this.user.firstName,
                    lastName: this.user.lastName,
                    dateOfBirth: this.user.dateOfBirth,
                    gender: this.user.gender
                };
                
                const result = validateItalianTaxCode(this.user.taxCode, userData);
                
                if (!result.valid) {
                    // Usa il messaggio dettagliato se disponibile, altrimenti l'errore generico
                    this.taxCodeError = result.message || result.error;
                    // Aggiorna il CF con la versione pulita se disponibile
                    if (result.cleanTaxCode && result.cleanTaxCode !== this.user.taxCode) {
                        this.user.taxCode = result.cleanTaxCode;
                    }
                } else {
                    this.taxCodeError = null;
                    // Aggiorna il CF con la versione pulita se disponibile
                    if (result.cleanTaxCode && result.cleanTaxCode !== this.user.taxCode) {
                        this.user.taxCode = result.cleanTaxCode;
                    }
                }
            } catch (error) {
                console.error('Errore nella validazione del codice fiscale:', error);
                this.taxCodeError = 'Errore nella validazione del codice fiscale';
            }
        },
        
        
        filterCountries() {
            // Il filteredCountries computed si aggiorna automaticamente
            // Questo metodo può essere usato per logica aggiuntiva se necessaria
        },
        
        togglePasswordVisibility() {
            this.showPassword = !this.showPassword;
        },
        checkPassword() {
            if (!this.validatePassword(this.user.password)) {
                this.passwordError = "La password deve essere lunga tra 8 e 20 caratteri e contenere almeno un numero, una lettera maiuscola, una lettera minuscola e nessuno spazio.";
            } else {
                this.passwordError = null;
            }
        },
        validatePassword(password) {
            const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
            return passwordPattern.test(password);
        },
        async checkEmailExists() {
            try {
                if (!this.user.email) {
                    this.emailError = 'Inserisci un\'email valida.';
                    return;
                }

                const response = await fetch(`https://api.elitefitness.it/api/users/exists?email=${encodeURIComponent(this.user.email)}`);

                if (!response.ok) {
                    throw new Error('Errore nella verifica dell\'email');
                }

                const result = await response.json();

                if (result.exists) {
                    this.emailError = 'Questa email è già registrata.';
                } else {
                    this.emailError = null;
                }
            } catch (error) {
                console.error('Errore nella verifica dell\'email:', error);
                this.emailError = 'Impossibile verificare l\'email. Riprova più tardi.';
            }
        },
        buildReturnUrl() {
            // Se c'è un freeEntryToken nella query, porta a /registration-complete per il deep link
            const freeEntryToken = this.$route.query.freeEntryToken;
            if (freeEntryToken) {
                return '/registration-complete';
            }
            
            // Se c'è un returnUrl nella query, usalo (es. per tornare alla pagina di redeem dopo la verifica email)
            // Altrimenti usa il default checkout
            const returnUrl = this.$route.query.returnUrl;
            if (returnUrl) {
                try {
                    // Decodifica se necessario e ritorna il path
                    const decoded = decodeURIComponent(returnUrl);
                    const [pathOnly] = decoded.split('?');
                    return pathOnly || '/checkout';
                } catch (e) {
                    console.error('Errore nel parsing del returnUrl:', e);
                    return '/checkout';
                }
            }
            return '/checkout';
        },
        showErrorAlert(message) {
            Swal.fire({
                title: 'Errore',
                text: message,
                icon: 'error',
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: true,
                allowEnterKey: true
            });
        },
        showEmailVerificationAlert() {
            Swal.fire({
                title: "Ti abbiamo inviato un'email con un link per verificare il tuo indirizzo.",
                text: 'Controlla la posta in arrivo (e la cartella spam) e segui le istruzioni per completare la verifica.',
                icon: 'info', // necessario perché iconHtml venga renderizzato
                iconHtml: '<i class="fa-solid fa-envelope"></i>',
                iconColor: '#e83539',
                showConfirmButton: true,
                confirmButtonText: 'OK',
                showCancelButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                didOpen: (popup) => {
                    const iconEl = popup.querySelector('.swal2-icon');
                    if (iconEl) {
                        iconEl.style.background = '#ffffff';
                        iconEl.style.border = 'none';
                        iconEl.style.color = '#e83539';
                        iconEl.style.display = 'flex';
                        iconEl.style.alignItems = 'center';
                        iconEl.style.justifyContent = 'center';
                    }
                    const iconContent = popup.querySelector('.swal2-icon .swal2-icon-content');
                    if (iconContent) {
                        iconContent.style.fontSize = '2.4rem';
                        iconContent.style.color = '#e83539';
                    }
                },
                customClass: {
                    popup: 'swal2-popup',
                    title: 'swal2-title',
                    content: 'swal2-content'
                }
            });
        },
        async register() {
            if (this.emailError) {
                this.showErrorAlert("L'email non è valida o è già in uso.");
                return;
            }
            if (this.passwordError){
                this.showErrorAlert("La password non soddisfa i requisiti minimi di sicurezza.");
                return;
            }
            if (!this.hasAcceptedTerms) {
                this.showErrorAlert('Per procedere accetta termini, privacy, regolamento, codice etico e dichiarazione sui documenti.');
                return;
            }
            
            // Se è Italia, il CF è obbligatorio e deve essere valido
            if (this.isItaly) {
                // Controlla che il CF sia presente
                if (!this.user.taxCode || this.user.taxCode.trim() === '') {
                    this.showErrorAlert('Il codice fiscale è obbligatorio per utenti italiani.');
                    this.taxCodeError = 'Il codice fiscale è obbligatorio';
                    return;
                }
                
                // Controlla che il CF abbia esattamente 16 caratteri
                if (this.user.taxCode.length !== 16) {
                    this.showErrorAlert('Il codice fiscale deve contenere esattamente 16 caratteri.');
                    this.taxCodeError = `Il codice fiscale deve contenere esattamente 16 caratteri. Caratteri inseriti: ${this.user.taxCode.length}`;
                    return;
                }
                
                // Valida sempre il CF prima del submit
                this.validateTaxCode();
                // Aspetta un momento per la validazione
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Se c'è un errore di validazione, blocca il submit
                if (this.taxCodeError) {
                    this.showErrorAlert('Il codice fiscale non è valido. Correggere gli errori prima di procedere.');
                    return;
                }
            }

            this.isLoading = true;
            try {
                const userPayload = { ...this.user };
                
                // Epurazione dei campi numeri di telefono: rimuove gli spazi
                if (userPayload.phoneNumber) {
                    userPayload.phoneNumber = userPayload.phoneNumber.replace(/\s+/g, '');
                }
                if (userPayload.mobileNumber) {
                    userPayload.mobileNumber = userPayload.mobileNumber.replace(/\s+/g, '');
                }
                if (userPayload.legalGuardian?.phoneNumber) {
                    userPayload.legalGuardian.phoneNumber = userPayload.legalGuardian.phoneNumber.replace(/\s+/g, '');
                }
                userPayload.dateOfBirth = dateOfBirthToApiIso(userPayload.dateOfBirth);
                
                if (!this.isMinor) {
                    delete userPayload.legalGuardian;
                }

                const acceptances = {
                    terms: true,
                    privacy: true,
                    regulation: true,
                    ethical: true,
                    identityDocs: true
                };
                const challengePayload = {
                    email: String(userPayload.email || '').trim().toLowerCase(),
                    firstName: String(userPayload.firstName || '').trim().toUpperCase(),
                    lastName: String(userPayload.lastName || '').trim().toUpperCase(),
                    dateOfBirth: userPayload.dateOfBirth,
                    placeOfBirth: (userPayload.placeOfBirth || '').trim().toUpperCase() || undefined,
                    taxCode: String(userPayload.taxCode || '').trim().toUpperCase(),
                    gender: userPayload.gender,
                    address: (userPayload.address || '').trim().toUpperCase() || undefined,
                    city: String(userPayload.city || '').trim().toUpperCase(),
                    postalCode: String(userPayload.postalCode || '').trim(),
                    province: String(userPayload.province || '').trim().toUpperCase(),
                    country: String(userPayload.country || '').trim(),
                    phoneNumber: String(userPayload.phoneNumber || '').replace(/\s+/g, '').toUpperCase(),
                    mobileNumber: (userPayload.mobileNumber || '').replace(/\s+/g, '').toUpperCase() || undefined,
                    postConfirmationRoute: this.buildReturnUrl(),
                    acceptances
                };
                if (this.isMinor && userPayload.legalGuardian) {
                    const g = userPayload.legalGuardian;
                    challengePayload.legalGuardian = {
                        firstName: String(g.firstName || '').trim().toUpperCase(),
                        lastName: String(g.lastName || '').trim().toUpperCase(),
                        taxCode: String(g.taxCode || '').trim().toUpperCase(),
                        relationship: String(g.relationship || '').trim(),
                        email: String(g.email || '').trim().toLowerCase(),
                        phoneNumber: String(g.phoneNumber || '').replace(/\s+/g, '').toUpperCase()
                    };
                }

                const signingExtras = await buildRegistrationSigningAttachments({ challengePayload });

                // Includi freeEntryToken se presente nella query
                const freeEntryToken = this.$route.query.freeEntryToken;
                const registerPayload = {
                    ...userPayload,
                    postConfirmationRoute: this.buildReturnUrl(),
                    ...signingExtras
                };
                if (freeEntryToken) {
                    registerPayload.freeEntryToken = freeEntryToken;
                }
                
                const response = await fetch('https://api.elitefitness.it/api/auth/register', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(registerPayload)
                });

                if (!response.ok) {
                    let errorMsg = 'Errore durante la registrazione';
                    try {
                        const errorData = await response.json();
                        errorMsg =
                            errorData?.message || errorData?.detail || errorData?.error || errorMsg;
                    } catch (_) {
                        try {
                            errorMsg = await response.text();
                        } catch (_) {/* noop */}
                    }
                    throw new Error(errorMsg);
                }

                await response.json();
                console.log('Registrazione completata.');
                this.showEmailVerificationAlert();
            } catch (error) {
                console.error('Errore durante la registrazione:', error);
                this.showErrorAlert(error?.message || 'Si è verificato un errore. Riprova più tardi.');
                return; // Evita ulteriori azioni
            } finally {
                this.isLoading = false;
            }
        },
        updateDateOfBirth(date) {
            // Normalizza a Date per il calcolo dell'età
            try {
                this.user.dateOfBirth = date instanceof Date ? date : new Date(date);
            } catch (_) { /* no-op */ }
            console.log('Data di nascita aggiornata:', this.user.dateOfBirth);
            
            // Valida il codice fiscale se presente e se è Italia
            if (this.isItaly && this.user.taxCode && this.user.taxCode.length === 16) {
                this.validateTaxCode();
            }
        },
        
        onGenderChange() {
            // Valida il codice fiscale se presente e se è Italia
            if (this.isItaly && this.user.taxCode && this.user.taxCode.length === 16) {
                this.validateTaxCode();
            }
        },
        
        onFirstNameChange() {
            // Valida il codice fiscale se presente e se è Italia
            if (this.isItaly && this.user.taxCode && this.user.taxCode.length === 16) {
                this.validateTaxCode();
            }
        },
        
        onLastNameChange() {
            // Valida il codice fiscale se presente e se è Italia
            if (this.isItaly && this.user.taxCode && this.user.taxCode.length === 16) {
                this.validateTaxCode();
            }
        }
    }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600&display=swap');

body {
    font-family: 'Montserrat', sans-serif;
}

.form-control, .form-select {
    font-family: 'Montserrat', sans-serif;
    background-color: rgba(255, 255, 255, 0);
    border: 2px solid white;
    color: white;
    margin-bottom: 15px;
    height: 60px;
    padding: 0 10px;
    border-radius: 0;
}

.form-control::placeholder {
    color: rgba(255, 255, 255, 0.6);
}

.btn-custom {
    font-family: 'Montserrat', sans-serif;
    background-color: #e83539;
    color: white;
    border: none;
    padding: 10px 20px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
    border-radius: 0;
    font-size: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.btn-custom.loading {
    background-color: #cc2e31; /* Colore più chiaro durante il caricamento */
    cursor: not-allowed;
}

.btn-custom:disabled {
    cursor: not-allowed;
    background-color: #cc2e31;
}

.loading-dots {
    display: inline-flex;
    gap: 2px;
}

.loading-dots span {
    animation: blink 1.4s infinite;
}

.loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes blink {
    0%, 100% {
        opacity: 0.3;
    }
    50% {
        opacity: 1;
    }
}

.text-danger {
    color: #ff4d4f;
}

.action-select {
    color: #e83539;
    font-weight: bold;
}

.toggle-password {
    position: absolute;
    /* il testo resta dov'era (16px dal bordo: 10 di prima + 6 di padding predefinito del pulsante): il padding
       allarga il bersaglio a 44px senza spostarlo */
    right: 0;
    padding: 0 16px 0 10px;
    min-width: 44px;
    min-height: 44px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 0.9rem;
}

:deep(.dp__input) {
    background-color: rgba(255, 255, 255, 0);
    border: 2px solid white;
    color: white;
    height: 60px;
    border-radius: 0;
}

:deep(.dp__input::placeholder) {
    color: rgba(255, 255, 255, 0.6);
}

:deep(.dp__input:focus) {
    border-color: #e83539;
    box-shadow: 0 0 0 0.25rem rgba(232, 53, 57, 0.25);
}

:deep(.dp__main) {
    font-family: 'Montserrat', sans-serif;
}

:deep(.dp__active) {
    background-color: #e83539 !important;
}

/* Stili per autocompletamento indirizzi e città */
.address-suggestions-dropdown,
.city-suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #e83539;
    border-top: none;
    border-radius: 0 0 5px 5px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.suggestion-item {
    padding: 12px 15px;
    cursor: pointer;
    color: #333;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    transition: background-color 0.2s ease;
}

.suggestion-item:hover {
    background-color: rgba(232, 53, 57, 0.1);
    color: #e83539;
}

.suggestion-item:last-child {
    border-bottom: none;
}

.address-loading,
.city-loading {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #e83539;
    border-top: none;
    border-radius: 0 0 5px 5px;
    padding: 12px 15px;
    color: #333;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 10px;
}

.address-loading i,
.city-loading i {
    color: #e83539;
}

/* Stili per i nuovi dropdown ISTAT */
.place-of-birth-suggestions-dropdown,
.postal-code-suggestions-dropdown,
.province-suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #e83539;
    border-top: none;
    border-radius: 0 0 5px 5px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
}

.place-of-birth-loading,
.postal-code-loading,
.province-loading {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #e83539;
    border-top: none;
    border-radius: 0 0 5px 5px;
    padding: 12px 15px;
    color: #333;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 10px;
}

.place-of-birth-loading i,
.postal-code-loading i,
.province-loading i {
    color: #e83539;
}

/* Miglioramento per i campi con autocompletamento */
.form-group.position-relative {
    margin-bottom: 15px;
}

/* Stili per dropdown nazione */
.country-dropdown-container {
    position: relative;
}

.country-dropdown-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: white;
    transition: transform 0.2s ease;
}

.country-dropdown-icon.open {
    transform: translateY(-50%) rotate(180deg);
}

.country-dropdown-search {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.95);
    border: 2px solid #e83539;
    border-top: none;
    border-radius: 0 0 5px 5px;
    z-index: 1000;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.country-search-input {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0;
    margin-bottom: 0;
}

.country-dropdown-list {
    max-height: 300px;
    overflow-y: auto;
}

.country-dropdown-item {
    padding: 12px 15px;
    cursor: pointer;
    color: #333;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    transition: background-color 0.2s ease;
}

.country-dropdown-item:hover,
.country-dropdown-item.selected {
    background-color: rgba(232, 53, 57, 0.1);
    color: #e83539;
}

.country-dropdown-item:last-child {
    border-bottom: none;
}

.country-dropdown-search .no-results {
    padding: 12px 15px;
    color: #666;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    text-align: center;
}

.form-group.position-relative .form-control {
    margin-bottom: 0;
}

/* Stili per checkbox consensi */
.terms-section {
    margin: 20px 0;
}

.terms-checkbox {
    display: flex;
    align-items: center;
    gap: 5px;
    font-family: 'Montserrat', sans-serif;
    padding: 5px;
    border-radius: 0px;
    border: 0px;
    margin-top: 5px;
}

.custom-checkbox {
    appearance: none;
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    background-color: #2e348c;
    border: 2px solid rgba(255, 255, 255, 0.144) !important;
    outline: none;
    cursor: pointer;
    transition: background-color 0.3s ease;
    position: relative;
}

.custom-checkbox:checked {
    background-color: #e83539;
}

.custom-checkbox:checked::before {
    content: '\f00c';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    color: white;
    text-align: center;
    font-size: 20px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
}

.terms-cb {
    appearance: none;
    /* 24px è il minimo di WCAG 2.5.8; il bersaglio vero è più largo (::after sotto) */
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
    min-height: 24px !important;
    background-color: #2e348c;
    border: 2px solid rgba(255, 255, 255, 0.144) !important;
    outline: none;
    cursor: pointer;
    transition: background-color 0.3s ease;
    position: relative;
}

.terms-cb:checked {
    background-color: #e83539;
}

/* Area di tocco più larga della casella visibile (34×34, misurata), senza spostare niente. Il pseudo-elemento parte
   dal bordo interno (2px): -7px vuol dire 5px oltre la casella per lato, e così non si sovrappone alla casella della
   riga sotto (le righe stanno a 39px o più l'una dall'altra). */
.terms-cb::after {
    content: '';
    position: absolute;
    inset: -7px;
}

.terms-cb:checked::before {
    content: '\f00c';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    color: white;
    text-align: center;
    font-size: 16px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
}

.text-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.terms-label {
    color: white;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
}

.terms-label a {
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    /* Altezza di tocco ≥ 24px (la riga è alta 15px): il padding su un elemento in linea allarga l'area cliccabile
       e il rettangolo del link senza cambiare l'altezza delle righe. clone: anche ogni riga di un link a capo. */
    padding-block: 6px;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
}

.terms-warning {
    font-family: 'Montserrat', sans-serif;
    font-size: 0.8rem;
    font-style: italic;
    color: white;
}

.terms-warning a {
    color: white;
    text-decoration: underline;
}

/* L'aspetto degli SweetAlert vive in assets/css/sweetalert.css. Le regole
   che stavano qui erano scoped, quindi non raggiungevano nemmeno il popup
   (che SweetAlert monta fuori dal componente): codice morto che faceva
   solo credere di poter cambiare i dialoghi da qui. */
</style>
